/**
 * AgentStreamModal — real-time observability window for a running recursive agent.
 *
 * Shows a live stream of every cycle the agent runs:
 *   - Cycle start: what market data was fed in
 *   - Cycle end:   signal, confidence, reasoning, trade result, timing, next check-in
 *   - Status change events
 *   - Trade executions
 *
 * Triggered by clicking the "stream" icon on an AgentCard.
 * Opens its own dedicated WebSocket connection so events are never dropped
 * by React 18 state-update batching in the parent component.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  X, Activity, TrendingUp, TrendingDown, Minus, Zap, Clock,
  CheckCircle2, XCircle, ChevronsDown, ChevronDown, ChevronRight, Wrench,
} from 'lucide-react';
import { ToolCallRecord } from '../types/trading';
import { TradingWebSocket } from '../services/websocketService';
import AgentErrorBanner from './AgentErrorBanner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StreamEventType = 'status_change' | 'cycle_start' | 'cycle_end' | 'signal' | 'trade';

interface StreamEvent {
  id: string;
  type: StreamEventType;
  timestamp: string;
  cycle?: number;
  // cycle_start fields
  input_summary?: string;
  // cycle_end fields
  success?: boolean;
  signal?: string | null;
  confidence?: number | null;
  reasoning?: string | null;
  entry_price?: number | null;
  stop_loss?: number | null;
  take_profit?: number | null;
  next_check_in_seconds?: number | null;
  duration_ms?: number | null;
  trade_executed?: boolean;
  tool_calls_made?: ToolCallRecord[];
  error?: string | null;
  // status_change fields
  status?: string;
  // trade fields
  trade_symbol?: string;
  trade_side?: string;
  trade_price?: number;
  trade_pnl?: number | null;
  is_paper?: boolean;
}

interface AgentStreamModalProps {
  agentId: string;
  agentName: string;
  agentStatus: string;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MAX_EVENTS = 100;
let _idCounter = 0;
const nextId = () => `se_${++_idCounter}`;

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}

function fmtDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms.toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function SignalBadge({ signal }: { signal: string | null | undefined }) {
  if (!signal) return null;
  const cfg = {
    buy:  { cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', icon: <TrendingUp  className="w-3 h-3" /> },
    sell: { cls: 'bg-red-500/20 text-red-300 border-red-500/30',             icon: <TrendingDown className="w-3 h-3" /> },
    hold: { cls: 'bg-slate-500/20 text-slate-300 border-slate-500/30',       icon: <Minus        className="w-3 h-3" /> },
  }[signal.toLowerCase()] ?? { cls: 'bg-slate-500/20 text-slate-300 border-slate-500/30', icon: null };

  return (
    <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-bold uppercase ${cfg.cls}`}>
      {cfg.icon}
      {signal.toUpperCase()}
    </span>
  );
}

function ConfidenceBar({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color = pct >= 75 ? 'bg-emerald-400' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-400">{pct}%</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CycleEndCard — expandable card for a completed cycle
// ---------------------------------------------------------------------------

function CycleEndCard({ ev }: { ev: StreamEvent }) {
  const [expanded, setExpanded] = useState(true);

  const signalColor = ev.signal === 'buy' ? 'border-l-emerald-500' : ev.signal === 'sell' ? 'border-l-red-500' : 'border-l-slate-600';

  return (
    <div className={`bg-slate-800/80 rounded-lg border border-slate-700 border-l-2 ${signalColor} overflow-hidden`}>
      {/* Header row */}
      <button
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-700/30 transition"
        onClick={() => setExpanded(e => !e)}
      >
        {ev.success
          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
          : <XCircle      className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
        }
        <span className="text-xs text-slate-400 font-mono">Cycle {ev.cycle}</span>
        {ev.signal && <SignalBadge signal={ev.signal} />}
        {ev.confidence != null && <ConfidenceBar value={ev.confidence} />}
        {ev.tool_calls_made && ev.tool_calls_made.length > 0 && (
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 text-xs border border-violet-500/30">
            <Wrench className="w-2.5 h-2.5" />
            {ev.tool_calls_made.length}
          </span>
        )}
        {ev.trade_executed && (
          <span className="ml-auto px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-xs border border-amber-500/30">
            TRADE
          </span>
        )}
        <span className="ml-auto text-xs text-slate-500 font-mono">{fmtDuration(ev.duration_ms)}</span>
        {expanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-3 pb-3 flex flex-col gap-2 border-t border-slate-700/50">
          {/* Tool calls made during this cycle */}
          {ev.tool_calls_made && ev.tool_calls_made.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-2 mb-1">
                <Wrench className="w-3 h-3" />
                Tool calls ({ev.tool_calls_made.length})
              </div>
              <div className="flex flex-col gap-1">
                {ev.tool_calls_made.map((tc, i) => (
                  <div key={i} className="bg-slate-900/80 rounded px-2 py-1.5 border border-slate-700/60">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-violet-300">{tc.name}</span>
                      {Object.entries(tc.args).map(([k, v]) => (
                        <span key={k} className="text-xs text-slate-500">
                          <span className="text-slate-400">{k}=</span>
                          <span className="text-slate-300 font-mono">{String(v)}</span>
                        </span>
                      ))}
                    </div>
                    {tc.result_preview && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{tc.result_preview}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {ev.reasoning && (
            <div>
              <div className="text-xs text-slate-500 mt-2 mb-1">Reasoning</div>
              <p className="text-xs text-slate-300 leading-relaxed">{ev.reasoning}</p>
            </div>
          )}
          {(ev.entry_price || ev.stop_loss || ev.take_profit) && (
            <div className="flex gap-4 text-xs">
              {ev.entry_price && (
                <span className="text-slate-400">Entry <span className="text-white font-mono">{ev.entry_price.toLocaleString()}</span></span>
              )}
              {ev.stop_loss && (
                <span className="text-slate-400">SL <span className="text-red-300 font-mono">{ev.stop_loss.toLocaleString()}</span></span>
              )}
              {ev.take_profit && (
                <span className="text-slate-400">TP <span className="text-emerald-300 font-mono">{ev.take_profit.toLocaleString()}</span></span>
              )}
            </div>
          )}
          {ev.next_check_in_seconds && (
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Clock className="w-3 h-3" />
              Next cycle in {ev.next_check_in_seconds}s
            </div>
          )}
          <AgentErrorBanner error={ev.error} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

const AgentStreamModal: React.FC<AgentStreamModalProps> = ({
  agentId, agentName, agentStatus, onClose,
}) => {
  const [events, setEvents] = useState<StreamEvent[]>([]);
  const [currentStatus, setCurrentStatus] = useState(agentStatus);
  const [cycleCount, setCycleCount] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Track processed event IDs to prevent StrictMode double-processing.
  // Using a Set stored in a ref so it persists across re-renders without
  // causing re-renders itself.
  const processedIdsRef = useRef<Set<string>>(new Set());

  const pushEvent = useCallback((ev: StreamEvent) => {
    setEvents(prev => {
      const next = [...prev, ev];
      return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
    });
  }, []);

  // Own dedicated WebSocket — bypasses React 18 state-update batching that
  // would drop intermediate messages when the parent passes events as props.
  useEffect(() => {
    const ws = new TradingWebSocket((rawEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = rawEvent as unknown as Record<string, any>;
      const data = (msg.data ?? {}) as Record<string, any>;

      // Filter to events for this agent only
      if (data.agent_id !== agentId) return;

      // Build a stable dedup key from meaningful fields so StrictMode
      // double-invocation of the WS onMessage doesn't add duplicate rows.
      const ts   = (data.timestamp as string) ?? '';
      const dedupKey = `${msg.type}|${ts}|${data.cycle ?? ''}|${data.signal ?? ''}`;
      if (processedIdsRef.current.has(dedupKey)) return;
      processedIdsRef.current.add(dedupKey);
      // Keep the set bounded
      if (processedIdsRef.current.size > 500) processedIdsRef.current.clear();

      const evTs = ts || new Date().toISOString();

      if (msg.type === 'agent_status_change') {
        const newStatus = data.status as string;
        setCurrentStatus(newStatus);
        pushEvent({ id: nextId(), type: 'status_change', timestamp: evTs, status: newStatus });

      } else if (msg.type === 'agent_cycle_start') {
        if (!sessionId) setSessionId(data.session_id as string);
        pushEvent({
          id: nextId(), type: 'cycle_start', timestamp: evTs,
          cycle: data.cycle as number,
          input_summary: data.input_summary as string,
        });

      } else if (msg.type === 'agent_cycle_end') {
        setCycleCount(c => c + 1);
        pushEvent({
          id: nextId(), type: 'cycle_end', timestamp: evTs,
          cycle:                  data.cycle as number,
          success:                data.success as boolean,
          signal:                 data.signal as string | null,
          confidence:             data.confidence as number | null,
          reasoning:              data.reasoning as string | null,
          entry_price:            data.entry_price as number | null,
          stop_loss:              data.stop_loss as number | null,
          take_profit:            data.take_profit as number | null,
          next_check_in_seconds:  data.next_check_in_seconds as number | null,
          duration_ms:            data.duration_ms as number | null,
          trade_executed:         data.trade_executed as boolean,
          tool_calls_made:        (data.tool_calls_made as ToolCallRecord[]) || [],
          error:                  data.error as string | null,
        });

      } else if (msg.type === 'agent_signal') {
        pushEvent({
          id: nextId(), type: 'signal', timestamp: evTs,
          signal:     data.signal as string,
          confidence: data.confidence as number,
        });

      } else if (msg.type === 'trade_created') {
        pushEvent({
          id: nextId(), type: 'trade', timestamp: evTs,
          trade_symbol: data.symbol as string,
          trade_side:   data.side as string,
          trade_price:  data.price as number,
          trade_pnl:    data.pnl as number | null,
          is_paper:     data.is_paper as boolean,
        });
      }
    });

    ws.connect();
    return () => ws.disconnect();
  // agentId is stable for the lifetime of this modal — safe to include
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events, autoScroll]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 60);
  };

  const statusColor = currentStatus === 'running' ? 'text-emerald-400' : currentStatus === 'paused' ? 'text-amber-400' : 'text-slate-400';
  const statusDot = currentStatus === 'running' ? 'bg-emerald-400 animate-pulse' : currentStatus === 'paused' ? 'bg-amber-400' : 'bg-slate-500';

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl flex flex-col shadow-2xl max-h-[90vh]">

        {/* ---- Header ---- */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-700/60">
          <div className="p-2 rounded-lg bg-green-400/10">
            <Activity className="w-4 h-4 text-green-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-white text-sm truncate">{agentName}</h2>
              <span className="flex items-center gap-1 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
                <span className={statusColor}>{currentStatus}</span>
              </span>
              {cycleCount > 0 && (
                <span className="text-xs text-slate-500 font-mono">{cycleCount} cycle{cycleCount !== 1 ? 's' : ''}</span>
              )}
            </div>
            {sessionId && (
              <div className="text-xs text-slate-600 font-mono mt-0.5 truncate">session {sessionId.slice(0, 8)}…</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ---- Event stream ---- */}
        <div className="flex-1 min-h-0 relative">
          {/* Scroll container — plain block so overflow-y works correctly */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="absolute inset-0 overflow-y-auto"
          >
            <div className="p-4 flex flex-col gap-2">
              {events.length === 0 && (
                <div className="flex flex-col items-center justify-center h-40 gap-3 text-slate-500">
                  <Zap className="w-8 h-8 opacity-30" />
                  <div className="text-sm">Waiting for agent activity…</div>
                  <div className="text-xs">Events will appear here as the agent runs cycles.</div>
                </div>
              )}

              {events.map(ev => {
                if (ev.type === 'cycle_end') {
                  return <CycleEndCard key={ev.id} ev={ev} />;
                }

                if (ev.type === 'cycle_start') {
                  return (
                    <div key={ev.id} className="flex items-start gap-2 py-1">
                      <span className="text-xs text-slate-600 font-mono w-16 flex-shrink-0">{fmtTime(ev.timestamp)}</span>
                      <div className="flex-1">
                        <span className="text-xs text-slate-500">
                          ▶ Cycle {ev.cycle} started
                        </span>
                        {ev.input_summary && (
                          <div className="text-xs text-slate-400 mt-0.5 leading-relaxed">{ev.input_summary}</div>
                        )}
                      </div>
                    </div>
                  );
                }

                if (ev.type === 'status_change') {
                  const sc = ev.status;
                  const scColor = sc === 'running' ? 'text-emerald-400' : sc === 'paused' ? 'text-amber-400' : 'text-slate-400';
                  return (
                    <div key={ev.id} className="flex items-center gap-2 py-0.5">
                      <span className="text-xs text-slate-600 font-mono w-16 flex-shrink-0">{fmtTime(ev.timestamp)}</span>
                      <span className={`text-xs font-medium ${scColor}`}>
                        ● Agent {sc}
                      </span>
                    </div>
                  );
                }

                if (ev.type === 'trade') {
                  const sideColor = ev.trade_side === 'buy' ? 'text-emerald-400' : 'text-red-400';
                  return (
                    <div key={ev.id} className="flex items-center gap-2 py-0.5">
                      <span className="text-xs text-slate-600 font-mono w-16 flex-shrink-0">{fmtTime(ev.timestamp)}</span>
                      <span className="text-xs text-amber-400">⚡ Trade executed</span>
                      <span className={`text-xs font-bold ${sideColor}`}>{ev.trade_side?.toUpperCase()}</span>
                      <span className="text-xs text-slate-300 font-mono">{ev.trade_symbol}</span>
                      <span className="text-xs text-slate-400">@ {ev.trade_price?.toLocaleString()}</span>
                      {ev.is_paper && <span className="text-xs text-slate-500">[paper]</span>}
                      {ev.trade_pnl != null && (
                        <span className={`text-xs font-mono ${ev.trade_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {ev.trade_pnl >= 0 ? '+' : ''}{ev.trade_pnl.toFixed(2)}
                        </span>
                      )}
                    </div>
                  );
                }

                return null;
              })}

              <div ref={bottomRef} />
            </div>
          </div>

          {/* Floating scroll-to-bottom button — appears when scrolled up */}
          {!autoScroll && (
            <button
              onClick={() => {
                setAutoScroll(true);
                bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-700/90 hover:bg-slate-600 border border-slate-500/50 text-xs text-slate-200 shadow-lg backdrop-blur-sm transition hover:shadow-emerald-500/10"
            >
              <ChevronsDown className="w-3.5 h-3.5 text-emerald-400" />
              Jump to latest
            </button>
          )}
        </div>

        {/* ---- Footer ---- */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-700/60 text-xs text-slate-500">
          <span>Live stream · last {MAX_EVENTS} events</span>
          <span className={autoScroll ? 'text-emerald-500' : 'text-slate-600'}>
            {autoScroll ? '● auto-scroll on' : '○ scrolled up'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AgentStreamModal;
